"use client";

import * as React from "react";
import { cn } from "@/lib/cn";
import { ASSET, PARTS, ROBOT_ANSWERS } from "./robot-stage";
import { FitText } from "./fit-text";

export type RobotMark = { idx: number; ok: boolean } | null;

/**
 * Buồng chế tạo: robot mờ + các bộ phận hiện rõ khi đã lắp, kèm vùng bấm cho từng
 * bộ phận. Tự định vị theo khung 16:9 (giống nhau ở chơi thử / giao về nhà / chơi
 * trên lớp) — nhờ đó tỉ lệ luôn nhất quán.
 */
export function RobotChamber({
  total,
  isDone,
  onPick,
}: {
  total: number;
  isDone: (i: number) => boolean;
  onPick: (i: number) => void;
}) {
  const parts = PARTS.slice(0, total);
  return (
    <div className="rbt-chamber absolute z-[2]" style={{ left: "35%", top: "5%", width: "32%", height: "91%" }}>
      <div className="rbt-scan" />
      <div className="absolute left-1/2 top-[2%] h-[88%] w-[84%] -translate-x-1/2">
        <img src={`${ASSET}/robot_mo.webp`} alt="" className="rbt-layer" style={{ opacity: 0.9 }} aria-hidden />
        {parts.map((p, i) => (
          <img
            key={p.key}
            src={`${ASSET}/robot_${p.key}.webp`}
            alt=""
            aria-hidden
            className={cn("rbt-layer rbt-part", isDone(i) && "visible")}
          />
        ))}
        {parts.map((p, i) => (
          <button
            key={p.key}
            type="button"
            onClick={() => onPick(i)}
            disabled={isDone(i)}
            aria-label={`Chọn ${p.label}${isDone(i) ? " (đã lắp)" : ""}`}
            className="rbt-hotspot"
            style={p.pos}
          />
        ))}
      </div>
      <div className="rbt-platform" />
    </div>
  );
}

/**
 * Khung câu hỏi robot (tên bộ phận + câu hỏi trong khung, 4 đáp án ảnh). Tự định
 * vị theo khung 16:9. `mark` để tô đúng/sai; `disabled` khoá đáp án sau khi chọn.
 */
export function RobotQuestionArea({
  partLabel,
  prompt,
  options,
  mark,
  onAnswer,
  disabled,
}: {
  partLabel: string;
  prompt: string;
  options: string[];
  mark: RobotMark;
  onAnswer: (i: number) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div className="absolute" style={{ left: "16%", top: "2%", width: "76%", height: "48%" }}>
        <img src={`${ASSET}/khung_cau_hoi.webp`} alt="" className="absolute inset-0 h-full w-full object-fill" aria-hidden />
        <div className="rbt-partname absolute" style={{ left: "41%", right: "41%", top: "2%", height: "22%" }}>
          <FitText text={partLabel} maxPx={22} minPx={9} />
        </div>
        <div className="rbt-qtext absolute" style={{ left: "8%", right: "7%", top: "31%", bottom: "8%" }}>
          <FitText text={prompt} maxPx={44} minPx={14} />
        </div>
      </div>

      <div className="absolute grid grid-cols-2 grid-rows-2" style={{ left: "17%", right: "4%", top: "51%", bottom: "4%", gap: "2% 2.4%" }}>
        {ROBOT_ANSWERS.map((a, i) => {
          const opt = options[i];
          if (opt === undefined) return <span key={i} aria-hidden />;
          const state = mark?.idx === i ? (mark.ok ? "correct" : "wrong") : "";
          return (
            <button key={i} type="button" onClick={() => onAnswer(i)} disabled={disabled} className={cn("rbt-answer", state)}>
              <img src={a.img} alt="" aria-hidden />
              <div className="rbt-atext absolute" style={{ left: "22%", right: "7%", top: "10%", bottom: "10%", display: "flex", alignItems: "center" }}>
                <FitText text={opt} maxPx={26} minPx={11} />
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
